import { useCallback, useMemo, useRef } from 'react';

import type { ActionCreator, AnyAction } from 'redux';
import type { UseActionStateParams } from '~/hooks/useActionState';

import useActionState from '~/hooks/useActionState';
import { useAppDispatch } from '~/modules/store';

export const useAsyncLegacyAction = <T extends ActionCreator<AnyAction>>(actionCreator: T) => {
  const dispatch = useAppDispatch();
  const actionPromiseRef = useRef<{
    resolve: UseActionStateParams['onSuccess'];
    reject: UseActionStateParams['onFailure'];
  }>();
  const actionState = useActionState(actionCreator, {
    onSuccess: (...args) => {
      actionPromiseRef.current?.resolve?.(...args);
    },
    onFailure: (...args) => {
      actionPromiseRef.current?.reject?.(...args);
    },
  });

  const handleAction = useCallback(
    (...args: unknown[]) => {
      const promise = new Promise<void>((resolve, reject) => {
        actionPromiseRef.current = { resolve, reject };
      });

      dispatch(actionCreator(...args));
      return promise;
    },
    [dispatch, actionCreator],
  );

  return useMemo(() => [handleAction, actionState] as const, [handleAction, actionState]);
};
